import { useRef, useCallback, useEffect } from 'react'
import type { ChangeEvent, ClipboardEvent, KeyboardEvent } from 'react'
import { cn } from './cn'

export interface OtpInputProps {
  length?: number
  value?: string
  onChange: (next: string) => void
  disabled?: boolean
  className?: string
  /** Focus the first digit box when mounted or when this becomes true. */
  autoFocus?: boolean
  /** Fired once the filled value reaches `length` digits (typing or paste). */
  onComplete?: (code: string) => void
  'aria-describedby'?: string
  'aria-invalid'?: boolean | 'true' | 'false'
}

export function OtpInput({
  length = 6,
  value = '',
  onChange,
  disabled = false,
  className = '',
  autoFocus = false,
  onComplete,
  'aria-describedby': ariaDescribedBy,
  'aria-invalid': ariaInvalid,
}: OtpInputProps) {
  const inputsRef = useRef<Array<HTMLInputElement | null>>([])
  const lastCompletedValueRef = useRef<string | null>(null)

  useEffect(() => {
    if (!autoFocus || disabled) return
    inputsRef.current[0]?.focus()
  }, [autoFocus, disabled])
  useEffect(() => {
    if (value.length < length) lastCompletedValueRef.current = null
  }, [value, length])

  const emitChange = useCallback((next: string) => {
    onChange(next)
    if (next.length < length) {
      lastCompletedValueRef.current = null
    } else if (next.length === length && next !== lastCompletedValueRef.current) {
      lastCompletedValueRef.current = next
      onComplete?.(next)
    }
  }, [length, onChange, onComplete])

  const handleChange = useCallback((index: number, e: ChangeEvent<HTMLInputElement>) => {
    const char = e.target.value.slice(-1)
    if (char && !/^\d$/.test(char)) return

    const arr = value.padEnd(length, ' ').split('')
    arr[index] = char || ' '
    const next = arr.join('').replace(/ /g, '').slice(0, length)
    emitChange(next)

    if (char && index < length - 1) {
      inputsRef.current[index + 1]?.focus()
    }
  }, [value, length, emitChange])

  const handleKeyDown = useCallback((index: number, e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace') {
      if (value[index]) {
        const next = value.slice(0, index) + value.slice(index + 1)
        emitChange(next)
        return
      }
      if (index > 0) {
        const previousIndex = index - 1
        const next = value.slice(0, previousIndex) + value.slice(previousIndex + 1)
        emitChange(next)
        inputsRef.current[previousIndex]?.focus()
      }
    }
  }, [value, emitChange])

  const handlePaste = useCallback((e: ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault()
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, length)
    if (pasted) {
      emitChange(pasted)
      const focusIdx = Math.min(pasted.length, length - 1)
      inputsRef.current[focusIdx]?.focus()
    }
  }, [length, emitChange])

  return (
    <div
      className={cn('flex flex-nowrap items-center justify-center gap-2 overflow-x-auto px-1', className)}
      role="group"
      aria-label="One-time password"
      aria-describedby={ariaDescribedBy}
      aria-invalid={ariaInvalid}
    >
      {Array.from({ length }, (_, i) => (
        <input
          key={`otp-digit-${i + 1}`}
          ref={(el) => { inputsRef.current[i] = el }}
          type="text"
          aria-label={`Digit ${i + 1} of ${length}`}
          inputMode="numeric"
          maxLength={1}
          value={value[i] || ''}
          onChange={(e) => handleChange(i, e)}
          onKeyDown={(e) => handleKeyDown(i, e)}
          onPaste={handlePaste}
          disabled={disabled}
          className="h-12 w-10 shrink-0 text-center text-lg font-semibold text-ink-body border border-line rounded-control sm:w-11
            focus-visible:outline-none focus:border-vjal-400 focus-visible:shadow-focus
            disabled:bg-whisper disabled:text-ink-muted transition-colors"
          autoComplete={i === 0 ? 'one-time-code' : 'off'}
        />
      ))}
    </div>
  )
}
